import { useMemo } from "react";

export interface PropertyGroup {
    name: string;
    properties: [string, string][];
    /**
     * The object's ungrouped base properties. Their search name is the bare property name,
     * without the group prefix. `name` is localized, so it can't be used to detect this.
     */
    isGeneral?: boolean;
}

export interface UsePropertyFilteringResult {
    filteredGroups: PropertyGroup[];
    hasSearchResults: boolean;
}

export function usePropertyFiltering(
    groupedProperties: PropertyGroup[],
    searchTerm: string = "",
): UsePropertyFilteringResult {
    const filteredGroups = useMemo(() => {
        if (!searchTerm.trim()) {
            return groupedProperties;
        }

        const searchLower = searchTerm.toLowerCase();
        return groupedProperties
            .map((group) => {
                const matchingProperties = group.properties.filter(
                    ([propName, propValue]) =>
                        propName.toLowerCase().includes(searchLower) || propValue.toLowerCase().includes(searchLower),
                );

                return matchingProperties.length > 0 ? { ...group, properties: matchingProperties } : null;
            })
            .filter((group): group is PropertyGroup => group !== null);
    }, [groupedProperties, searchTerm]);

    const hasSearchResults = filteredGroups.length > 0;

    return {
        filteredGroups,
        hasSearchResults,
    };
}
